import axios from 'axios';
import { PaymentRequest, SetupIntentRequest, PAYMENT_STATUS, Payment } from '@finpay-payment-v3/payment-types';
import { getPaymentApiBaseUrl, getPaymentApiPathPrefix } from './api-config';

export interface CreatePaymentParams {
  programId: number;
  paymentRequest: PaymentRequest;
}

export interface CreatePaymentResult {
  success: boolean;
  message: string;
  paymentIntentId?: string;
  clientSecret?: string;
  status?: string;
  payment?: Payment;
}

export const getPayment = async (
    params: {
      paymentId: number
    }
): Promise<Payment> => {

  try{

    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/payment/${params.paymentId}`;

    const response = await axios.get(url);

    return response.data as Payment

  }catch(e){

    throw new Error(
      "Error while fetching payment",
      e instanceof Error ? { cause: e } : undefined
    )

  }

}

export const createPayment = async (
  params: CreatePaymentParams
): Promise<CreatePaymentResult> => {
  const { programId, paymentRequest } = params;

  try {
    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/program/${programId}/payment`;
    
    const response = await axios.post(url, paymentRequest);

    const entity = response.data;
    if (!entity) {
      return {
        success: false,
        message: 'Failed to create payment.',
      };
    }

    const paymentId = entity.paymentId || entity.id || entity.paymentIntentId || entity.extPaymentIntentId || entity.client_secret?.split('_secret_')[0];

    if (!paymentId) {
      return {
        success: false,
        message: 'Payment created but no payment ID returned.',
      };
    }

    const paymentStatus = (entity.status || entity.paymentStatus || '');
    const isFailed = paymentStatus.toLowerCase() === PAYMENT_STATUS.FAILED.toLowerCase();
    const isPending = paymentStatus.toLowerCase() === PAYMENT_STATUS.PENDING.toLowerCase();

    if (isFailed) {
      let declineMessage = 'Payment was declined.';
      if (entity.externalResponse) {
        try {
          const parsed = typeof entity.externalResponse === 'string' 
            ? JSON.parse(entity.externalResponse) 
            : entity.externalResponse;
          declineMessage = parsed.message || declineMessage;
        } catch {
          declineMessage = 'Payment was declined.';
        }
      }
      return {
        success: false,
        message: declineMessage,
        paymentIntentId: String(paymentId),
        status: paymentStatus,
        payment: entity as Payment,
      };
    }

    const successMessage = isPending 
      ? 'Payment submitted, please check back later for status.'
      : 'Payment successful';

    return {
      success: true,
      message: successMessage,
      paymentIntentId: String(paymentId),
      clientSecret: entity.client_secret,
      status: entity.status || entity.paymentStatus,
      payment: entity as Payment,
    };
  } catch (err: any) {
    const responseData = err.response?.data;
    let errorMessage = 'An unexpected error occurred creating payment.';
    if (responseData?.externalResponse) {
      try {
        const parsed = typeof responseData.externalResponse === 'string'
          ? JSON.parse(responseData.externalResponse)
          : responseData.externalResponse;
        errorMessage = parsed.message || errorMessage;
      } catch {
        errorMessage = responseData?.message || err.message || errorMessage;
      }
    } else {
      errorMessage = responseData?.message || err.message || errorMessage;
    }
    return {
      success: false,
      message: errorMessage,
    };
  }
};

export interface CreateSetupIntentParams extends SetupIntentRequest {}

export interface CreateSetupIntentResult {
  success: boolean;
  message: string;
  setupIntentId?: string;
  clientSecret?: string;
  status?: string;
}

export const createSetupIntent = async (
  params: CreateSetupIntentParams = {}
): Promise<CreateSetupIntentResult> => {
  const { extPaymentUserId, paymentMethodTypeId } = params;

  try {
    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/setup-intent`;
    
    const requestBody: { 
      extPaymentUserId?: string; 
      paymentMethodTypeId?: number;
    } = {};
    
    if (extPaymentUserId) {
      requestBody.extPaymentUserId = extPaymentUserId;
    }
    
    if (paymentMethodTypeId) {
      requestBody.paymentMethodTypeId = paymentMethodTypeId;
    }

    const response = await axios.post(url, requestBody);

    const entity = response.data;
    if (!entity) {
      return {
        success: false,
        message: 'Failed to create setup intent.',
      };
    }

    const setupIntentId = entity.id || entity.client_secret?.split('_secret_')[0];

    return {
      success: true,
      message: 'Setup intent created.',
      setupIntentId,
      clientSecret: entity.client_secret,
      status: entity.status,
    };
  } catch (err: any) {
    const responseData = err.response?.data;
    const errorMessage = 
      responseData?.externalResponse?.message || 
      responseData?.message || 
      err.message || 
      'An unexpected error occurred creating setup intent.';
    return {
      success: false,
      message: errorMessage,
    };
  }
};
